import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Layers } from 'lucide-react'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button, Input } from '@brix/ui'
import { useAuthStore } from '@/store/authStore'

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'At least 8 characters'),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, { message: "Passwords don't match", path: ['confirm'] })
type FormData = z.infer<typeof schema>

const LANDING_URL = import.meta.env['VITE_LANDING_URL'] ?? 'https://brix.app'
const TURNSTILE_SITE_KEY = import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined

export function SignUpPage() {
  const [error, setError] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const { signUp, isLoading } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setError(null)
    try {
      await signUp(data.email, data.password, captchaToken ?? undefined)
      if (inviteToken) {
        void navigate(`/invite/${inviteToken}`, { replace: true })
      } else {
        void navigate('/onboarding')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign up failed')
      setCaptchaToken(null)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-violet-600">
            <Layers className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Create your account</h1>
          <p className="mt-1 text-sm text-gray-500">
            {inviteToken ? 'Create an account to accept your invitation' : 'Start building websites in minutes'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Email" type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
          <Input label="Password" type="password" autoComplete="new-password" {...register('password')} error={errors.password?.message} />
          <Input label="Confirm password" type="password" autoComplete="new-password" {...register('confirm')} error={errors.confirm?.message} />

          {TURNSTILE_SITE_KEY && (
            <Turnstile
              siteKey={TURNSTILE_SITE_KEY}
              options={{ action: 'turnstile-spin-v1' }}
              onSuccess={setCaptchaToken}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />
          )}

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

          <Button
            type="submit"
            loading={isLoading}
            disabled={Boolean(TURNSTILE_SITE_KEY) && !captchaToken}
            className="w-full"
          >
            Create account
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-gray-400">
          By creating an account you agree to our{' '}
          <a href={`${LANDING_URL}/terms`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href={`${LANDING_URL}/privacy`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
            Privacy Policy
          </a>.
        </p>

        <p className="mt-6 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <Link
            to={inviteToken ? `/login?invite=${inviteToken}` : '/login'}
            className="text-violet-600 hover:underline font-medium"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
